import { PlusIcon } from "@heroicons/react/solid"
import { useTable, useUpdate } from "@refinedev/core"
import { Breadcrumbs } from "components/breadcrumbs"
import Button from "components/button"
import { IPasscodeItem } from "interfaces"
import { UserType } from "interfaces"
import { useEffect, useMemo } from "react"
import toast from "react-hot-toast"
import { Link } from "react-router-dom"
import { Filter, PasscodeCard, PasscodeTable } from "./components"
import Pagination from "components/pagination"
import { FullScreenLoading } from "components/fullScreenLoading"
import { ExportButton } from "components/exportButton"
import { canAccessByType, isHaveActionPermission } from "utills/helpers"

export const PasscodeList: React.FC = () => {
  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();
  const isAdmin = canAccessByType(UserType.ADMIN);

  const {
    tableQuery,
    filters,
    setFilters,
  } = useTable<IPasscodeItem>({
    resource: "private-flow/event-passcodes",
    syncWithLocation: true,
    pagination: {
      mode: "off",
    },
    meta: {
      resource: "codes",
    },
  });

  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) =>
      "field" in item ? item : []
    );

    return {
      q: logicalFilters.find((item) => item.field === "q")?.value || "",
      event_id: logicalFilters.find((item) => item.field === "event_id")?.value || "",
      is_active: logicalFilters.find((item) => item.field === "is_active")?.value || "",
      private_flow_type: isAdmin ? logicalFilters.find((item) => item.field === "private_flow_type")?.value || "" : "",
      lte: logicalFilters.find((item) => item.field === "lte")?.value || "",
      gte: logicalFilters.find((item) => item.field === "gte")?.value || "",
    };
  }, [filters, isAdmin]);

  useEffect(() => {
    if (!isAdmin && filters.some((item) => "field" in item && item.field === "private_flow_type")) {
      setFilters(filters.flatMap((item) => ("field" in item && item.field !== "private_flow_type") ? item : []));
    }
  }, [filters, isAdmin, setFilters]);

  const _toggleActive = (id: string, isActive: boolean) => {
    if (!isHaveActionPermission("private_code_email", "events", "edit")) return;
    if (window.confirm(`Are you sure you want to ${!isActive ? "activate": "deactivate"} this passcode/email?`)) {
      updateMutation({
        resource: "private-flow/event-passcodes",
        id,
        meta: {
          URLSuffix: isActive ? "inactive" : "active",
          ignoreResourceWrapper: true
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
        },
      });
    }
  };

  const _handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap(item => "field" in item ? item: []);
    const pageIndex = updatedFilters.findIndex(filter => filter.field === "page");
    if (pageIndex < 0) {
      updatedFilters.push({field: "page", operator: "eq", value: page})
    } else {
      updatedFilters[pageIndex].value = page;
    }

    setFilters(updatedFilters);
  }
  
  return (
    <div className="wrapper">
      <Breadcrumbs
        additionalLabel={
          <div className="flex items-center gap-x-3">
            {isHaveActionPermission("private_code_email", "events", "export") ? (
              <ExportButton
                filters={filters}
                title="Export code/email"
                resource="export/event-passcodes"
                className="w-fit bg-white text-brand-500 font-bold py-2 border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85"
              />
            ) : <></>}
            {isHaveActionPermission("private_code_email", "events", "create") ? (
              <Link to={"/events/passcodes/create"}>
                <Button className="w-11 h-11 sm:w-auto">
                  <span className="sr-only">Create/Mass update</span>
                  <PlusIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">
                    Create/Mass update
                  </span>
                </Button>
              </Link>
            ) : <></>}
          </div>
        }
      />
      <div className="box-container">
        <Filter
          currentFilterValues={currentFilterValues}
          setFilters={setFilters}
        />
      </div>
      <div className="box-container">
        {!tableQuery.data?.data?.length &&
          !tableQuery.isFetching && (
            <div className="text-center">
              No data
            </div>
          )}
        {tableQuery?.data?.data && tableQuery?.data?.data.length > 0 ? (
          <>
            <div className="sm:hidden">
              <PasscodeCard
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
            <div className="hidden sm:block">
              <PasscodeTable
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
          </>
        ) : <></>}
        {tableQuery?.data?.paging ? (
          <div className="mt-6">
            <Pagination
              currentPage={tableQuery?.data?.paging.current_page}
              totalPage={tableQuery?.data?.paging.total_page}
              total={{
                totalItems: tableQuery?.data?.paging?.total_item ?? 0,
                total: tableQuery?.data?.paging.total
              }}
              onChange={_handlePageChanged}
            />
          </div>
        ) : <></>}
      </div>
      {(tableQuery.isLoading) || (tableQuery.isFetching && tableQuery.isPreviousData)|| updateMutationIsLoading ? <FullScreenLoading /> : <></>}
    </div>
  )
}
